import React from "react";

import { getStepBg } from "@/lib/catalog/getStepBg";
import { getStepIcon } from "@/lib/catalog/getStepIcon";
import { cn } from "@/lib/utils";

import type { SyncProgressViewerProps } from "./SyncProgressViewer.types";

export const SyncProgressViewer: React.FC<SyncProgressViewerProps> = ({
  steps,
  className,
}) => {
  const completedCount = steps.filter((s) => s.status === "completed").length;
  const hasFailedStep = steps.some((s) => s.status === "failed");
  const progressPercent =
    steps.length > 0 ? (completedCount / steps.length) * 100 : 0;

  return (
    <div data-testid="sync-progress" className={cn("space-y-4", className)}>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Sync Progress
          </span>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {completedCount} of {steps.length} steps
          </span>
        </div>
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              hasFailedStep
                ? "bg-rose-500"
                : "bg-gradient-to-r from-indigo-500 to-indigo-600",
            )}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        {steps.map((step) => (
          <div
            key={String(step.id)}
            data-testid={`sync-step-${step.id}`}
            className="flex items-center gap-3"
          >
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                getStepBg(step.status ?? ""),
              )}
            >
              {getStepIcon(step.status ?? "")}
            </div>
            <div className="flex-1">
              <p
                className={cn(
                  "text-sm font-medium",
                  step.status === "completed"
                    ? "text-slate-700 dark:text-slate-300"
                    : step.status === "in_progress"
                      ? "text-indigo-600 dark:text-indigo-400"
                      : step.status === "failed"
                        ? "text-rose-600 dark:text-rose-400"
                        : "text-slate-400 dark:text-slate-500",
                )}
              >
                {step.name}
              </p>
            </div>
            {step.status === "completed" && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                Done
              </span>
            )}
            {step.status === "in_progress" && (
              <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                Running...
              </span>
            )}
            {step.status === "failed" && (
              <span className="text-xs text-rose-600 dark:text-rose-400 font-medium">
                Failed
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
